export type OAuthProvider = 'google' | 'github'

export interface OAuthAuthorizationResponse {
  url: string
  state: string
}

function oauthStateStorageKey(provider: OAuthProvider): string {
  return `oauth_state_${provider}`
}

export function storeOAuthState(provider: OAuthProvider, state: string): void {
  sessionStorage.setItem(oauthStateStorageKey(provider), state)
}

// OAuth state is single-use in the browser. Removing it even on mismatch keeps
// stale or replayed callbacks from being accepted by a later login attempt.
export function consumeOAuthState(provider: OAuthProvider, returnedState: string): boolean {
  const key = oauthStateStorageKey(provider)
  const expectedState = sessionStorage.getItem(key)
  sessionStorage.removeItem(key)
  return expectedState !== null && expectedState === returnedState
}

export function oauthSuccessRedirect(isNewUser: boolean): '/welcome' | '/' {
  return isNewUser ? '/welcome' : '/'
}

export function oauthProviderLabel(provider: OAuthProvider): string {
  return provider === 'github' ? 'GitHub' : 'Google'
}

function isOAuthUnavailableMessage(rawMessage?: string | null): boolean {
  if (!rawMessage) {
    return false
  }
  return /oauth is not configured|auth engine is not configured/i.test(rawMessage)
}

// Public copy for login/register notifications. Backend messages such as
// "google OAuth is not configured" are operator-facing and must not appear on
// the website.
export function oauthStartErrorMessage(provider: OAuthProvider, rawMessage?: string | null): string {
  const name = oauthProviderLabel(provider)
  if (isOAuthUnavailableMessage(rawMessage)) {
    return `${name} sign-in is unavailable right now. Please continue with email, or try again later.`
  }
  return `Could not start ${name} sign-in. Please try again.`
}

export function oauthCallbackErrorMessage(provider: OAuthProvider, rawMessage?: string | null): string {
  const name = oauthProviderLabel(provider)
  if (isOAuthUnavailableMessage(rawMessage)) {
    return `${name} sign-in is unavailable right now. Please continue with email, or try again later.`
  }
  return `${name} login failed. Please try again.`
}
